"use client";

import React from "react";
import { Check, Minus, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function LeaseFlexibilityMatrix() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const comparisonData = [{
    criteria: "Lease Length",
    shortTerm: {
      text: "Flexible (1–11 Months)",
      isPremium: true
    },
    longTerm: {
      text: "Standard (12+ Months)",
      isPremium: false
    }
  }, {
    criteria: "Utility Inclusion",
    shortTerm: {
      text: "All Utilities Included",
      isPremium: true
    },
    longTerm: {
      text: "Tenant Responsibility",
      isPremium: false
    }
  }, {
    criteria: "Furniture & Decor",
    shortTerm: {
      text: "Fully Furnished",
      isPremium: true
    },
    longTerm: {
      text: "Unfurnished",
      isPremium: false
    }
  }, {
    criteria: "Maintenance",
    shortTerm: {
      text: "Full Coordination",
      isPremium: true
    },
    longTerm: {
      text: "Standard Repairs",
      isPremium: false
    }
  }, {
    criteria: "Internet & WiFi",
    shortTerm: {
      text: "High-Speed Included",
      isPremium: true
    },
    longTerm: {
      text: "Tenant Setup Required",
      isPremium: false
    }
  }, {
    criteria: "Ideal For",
    shortTerm: {
      text: "Travelers & Corporate",
      isPremium: false
    },
    longTerm: {
      text: "Long-term Residents",
      isPremium: false
    }
  }];
  return <section id="lease-flexibility-matrix" ref={ref} className={`py-24 bg-background transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mb-4">
            Find Your Perfect Fit
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Whether you need a temporary haven or a permanent home, we offer
            tailored leasing solutions to match your lifestyle. Compare our
            offerings to see which suits you best.
          </p>
        </div>

        <div className="max-w-5xl mx-auto bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <Table className="w-full">
            <TableHeader className="bg-muted/30">
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="w-1/3 py-6 px-6 text-lg font-semibold text-foreground">
                  Key Criteria
                </TableHead>
                <TableHead className="w-1/3 py-6 px-6 text-lg font-semibold text-secondary">
                  Short-Term Furnished
                </TableHead>
                <TableHead className="w-1/3 py-6 px-6 text-lg font-semibold text-foreground">
                  Long-Term Unfurnished
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonData.map((row, i) => <TableRow key={i} data-index={i} className="hover:bg-muted/10 border-b border-border last:border-0 transition-colors">
                  <TableCell className="py-5 px-6 font-medium text-foreground align-middle">
                    {row.criteria}
                  </TableCell>
                  <TableCell className="py-5 px-6 align-middle bg-secondary/5">
                    <div className="flex items-center gap-3">
                      {row.shortTerm.isPremium ? <Check className="w-5 h-5 text-secondary shrink-0" /> : <span className="w-5 h-5 block shrink-0" />}
                      <span className={row.shortTerm.isPremium ? "font-semibold text-foreground" : "text-muted-foreground"}>
                        {row.shortTerm.text}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-5 px-6 align-middle">
                    <div className="flex items-center gap-3">
                      {row.longTerm.isPremium ? <Check className="w-5 h-5 text-secondary shrink-0" /> : <span className="w-5 h-5 flex items-center justify-center shrink-0">
                          <Minus className="w-3 h-3 text-muted-foreground/50" />
                        </span>}
                      <span className="text-muted-foreground">
                        {row.longTerm.text}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-6">
            Still unsure which option is right for you?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto">
              
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/ReachOutPage">Contact Our Team</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}